import React, { useEffect, useState } from 'react';
import { Button, Spin, Typography } from 'antd';
import { Mic, MessageCircle, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSessionToken, useIsGuest, useEnterAssessment, useRevokedKickOut } from '@/hooks';
import { AnswerMode } from '@/store/interviewStore';
import {
  AssessmentService,
  AssessmentStatus,
  HtsService,
  BaseAPIClient,
  InterviewTemplatesService,
} from '@/services/api';
import type { InterviewTemplate, TemplateItemHistory } from '@/services/api';
import { ROUTES, buildRouteWithQuery } from '@/routes';
import { QuestionPicker, defaultQuestionSelection } from '@/components/Interview/QuestionPicker';
import { GradientPanel } from './GradientPanel';
import { InterviewPickerModal } from './InterviewPickerModal';
import {
  partsOf,
  autoSelectPart,
  canAttemptPart,
  canChooseQuestions,
  computeSteps,
  openParts,
  defaultModeFor,
  feedbackNotes,
  isLocked,
  partStatusText,
  wizardEntryFor,
  STEP_TITLES,
  type AssessmentsByTemplate,
  type PartKey,
  type TemplatePart,
  type StepKey,
} from './interviewStatus';
import type { WizardState } from '@/store';
import styles from './SetupWizard.module.scss';

const { Title } = Typography;

const PANEL_COPY: Record<string, string> = {
  pick: 'Every interview here is scored the way a real board scores it. Pick one and we’ll set the rest up in a few taps.',
  which: 'Take them in whichever order suits you. The one you don’t choose now stays here until you finish it.',
  questions:
    'Build your interview from the list. Pick as many or as few questions as you like; together they count as one interview. You’ll choose again from the same list each time you start.',
  questionsRetry:
    'Build your interview from the list. Pick as many or as few questions as you like; together they count as one interview. We’ve pre-selected the ones that could use another attempt, plus any you haven’t tried yet.',
  mode: 'Voice is closest to the real thing — you speak your answer against the clock. Chat lets you type and edit before sending. The scoring is identical either way.',
  preview: 'Nothing has started yet. You can stop at any point and pick up where you left off.',
  previewTwoPart:
    'Nothing has started yet. You can stop at any point and pick up where you left off, and switch to the other questionnaire without losing progress.',
  locked:
    'Packages are sets of interviews built around a role — patrol, dispatch, promotion boards. Unlock one and its interviews land here, ready to set up.',
};

const ABOUT_FALLBACK = 'No description was added for this interview yet.';

const selectTemplatePart = (template: InterviewTemplate) => {
  const part = autoSelectPart(template);
  return {
    templateId: template.id,
    part,
    mode: defaultModeFor(part),
  };
};

interface SetupWizardProps {
  templates: InterviewTemplate[];
  assessmentsByTemplate: AssessmentsByTemplate;
  loadingTemplates: boolean;
  loadError: string | null;
  onRetryLoad?: () => void;
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
  cardRef: React.RefObject<HTMLDivElement>;
}

export const SetupWizard: React.FC<SetupWizardProps> = ({
  templates,
  assessmentsByTemplate,
  loadingTemplates,
  loadError,
  onRetryLoad,
  wizard,
  setWizard,
  cardRef,
}) => {
  const navigate = useNavigate();
  const { getToken } = useSessionToken();
  const isGuest = useIsGuest();
  const enterAssessment = useEnterAssessment();
  const handleIfRevoked = useRevokedKickOut();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [htsProgress, setHtsProgress] = useState<{ answered: number; total: number } | null>(null);
  const [compProgress, setCompProgress] = useState<{ answered: number; total: number } | null>(null);

  // Question picker state, tagged with its template so switching interviews
  // never carries one's history or selection into another. A null history
  // means a first attempt.
  const [itemHistory, setItemHistory] = useState<{
    templateId: string;
    history: TemplateItemHistory | null;
  } | null>(null);
  const [questionChoice, setQuestionChoice] = useState<{
    templateId: string;
    selectedIds: string[];
    differentIds: string[];
  } | null>(null);

  const template = templates.find((t) => t.id === wizard.templateId) ?? null;
  const parts = template ? partsOf(template, assessmentsByTemplate) : [];
  const enrichedParts: TemplatePart[] = parts.map((p) => {
    if (p.key === 'hts' && htsProgress) return { ...p, qs: htsProgress.total, done: htsProgress.answered };
    if (p.key === 'comp' && compProgress) return { ...p, qs: compProgress.total, done: compProgress.answered };
    return p;
  });
  const partOf = (key: PartKey) => enrichedParts.find((p) => p.key === key);

  useEffect(() => {
    let cancelled = false;
    setHtsProgress(null);
    setCompProgress(null);
    if (!template) return;

    const currentParts = partsOf(template, assessmentsByTemplate);
    const htsPart = currentParts.find((p) => p.key === 'hts');
    const compPart = currentParts.find((p) => p.key === 'comp');

    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;

      if (htsPart?.status === 'prog') {
        try {
          const res = await new HtsService(token).getQuestionsForTemplate(template.id);
          if (!cancelled) {
            setHtsProgress({ answered: res.submission?.answers.length ?? 0, total: res.questions.length });
          }
        } catch {
          /* empty */
        }
      }

      if (compPart?.status === 'prog') {
        const row = (assessmentsByTemplate.get(template.id) ?? []).find(
          (a) => a.status === AssessmentStatus.InProgress
        );
        if (row) {
          try {
            const full = await new AssessmentService(token).getAssessment(row.id);
            if (!cancelled) {
              setCompProgress({
                answered: full.answeredQuestions?.length ?? 0,
                total: full.retakeItemIds.length > 0 ? full.retakeItemIds.length : full.interviewTemplate.items.length,
              });
            }
          } catch {
            /* empty */
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [template, assessmentsByTemplate, getToken]);

  const templateId = template?.id ?? null;
  const compStatus = parts.find((p) => p.key === 'comp')?.status;
  const hasEarlierAttempt = compStatus === 'done';
  const compChosen = wizard.part === 'comp';
  useEffect(() => {
    if (!templateId || isGuest || !compChosen) return;
    if (!hasEarlierAttempt) {
      setItemHistory({ templateId, history: null });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const history = await new InterviewTemplatesService(token).getItemHistory(templateId);
        if (!cancelled) setItemHistory({ templateId, history });
      } catch (err) {
        if (cancelled || handleIfRevoked(err)) return;
        // The picker still works without scores — it just starts with every
        // question selected, as on a first attempt.
        console.warn('Question history lookup failed:', BaseAPIClient.parseErrorMsg(err));
        if (!cancelled) setItemHistory({ templateId, history: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId, isGuest, compChosen, hasEarlierAttempt, getToken, handleIfRevoked]);

  const historyLoaded = !!templateId && itemHistory?.templateId === templateId;
  const pickerHistory = historyLoaded ? (itemHistory?.history ?? null) : null;
  // The candidate's edits, else the default selection once history has loaded.
  const questionSelection =
    questionChoice && questionChoice.templateId === templateId
      ? questionChoice
      : template && historyLoaded
        ? { selectedIds: defaultQuestionSelection(template.items, pickerHistory), differentIds: [] as string[] }
        : null;

  useEffect(() => {
    if (wizard.flashNonce === 0) return;
    const el = cardRef.current;
    if (!el) return;
    el.classList.remove(styles.flash);
    void el.offsetWidth;
    el.classList.add(styles.flash);
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [wizard.flashNonce, cardRef]);

  useEffect(() => {
    if (!isGuest || loadingTemplates || templates.length === 0) return;
    const guestTemplate = templates[0];
    const guestParts = partsOf(guestTemplate, assessmentsByTemplate);

    if (wizard.templateId !== guestTemplate.id) {
      setWizard((prev) => ({ ...prev, ...selectTemplatePart(guestTemplate), step: 0 }));
      return;
    }

    const currentPart = guestParts.find((p) => p.key === wizard.part);
    if (currentPart?.status === 'done' && wizard.step !== 0) {
      setWizard((prev) => ({ ...prev, part: null, mode: null, step: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, loadingTemplates, templates, assessmentsByTemplate, wizard.templateId, wizard.part, wizard.step]);

  const skipPick = isGuest && !!template;

  // A submitted HTS can't be reopened, so it can't stay the chosen part —
  // e.g. a persisted selection from before it was submitted in another tab.
  // Drop it and fall back to "Where to start".
  const selectedPart = wizard.part ? partOf(wizard.part) : undefined;
  const selectedLocked = !!selectedPart && isLocked(selectedPart);
  useEffect(() => {
    if (!selectedLocked || !template) return;
    const { part, mode } = wizardEntryFor(template, null);
    const which = Math.max(0, computeSteps(template, part, !skipPick).indexOf('which'));
    setWizard((prev) => ({ ...prev, part, mode, step: Math.min(prev.step, which) }));
  }, [selectedLocked, template, skipPick, setWizard]);

  const includeQuestions = wizard.part === 'comp' && !!compStatus && canChooseQuestions(isGuest, compStatus);
  const steps = computeSteps(template, wizard.part, !skipPick, includeQuestions);
  const stepIndex = Math.min(wizard.step, steps.length - 1);
  const stepKey = steps[stepIndex];
  const isLast = stepKey === 'preview';

  // The persisted step index can land past the picker without the candidate
  // having seen it — e.g. it was saved while resuming, before the picker step
  // existed, or their edits were lost on remount. Send them back to it once.
  const [pickerSeenFor, setPickerSeenFor] = useState<string | null>(null);
  const questionsIndex = steps.indexOf('questions');
  useEffect(() => {
    if (stepKey === 'questions' && templateId) setPickerSeenFor(templateId);
  }, [stepKey, templateId]);
  useEffect(() => {
    if (questionsIndex < 0 || stepIndex <= questionsIndex || pickerSeenFor === templateId) return;
    setWizard((prev) => ({ ...prev, step: questionsIndex }));
  }, [questionsIndex, stepIndex, pickerSeenFor, templateId, setWizard]);

  const canNext = (): boolean => {
    if (stepKey === 'pick') return !!template;
    if (stepKey === 'which') return !!wizard.part;
    if (stepKey === 'questions') return !!questionSelection && questionSelection.selectedIds.length > 0;
    if (stepKey === 'mode') return !!wizard.mode;
    return true;
  };

  const pickInterview = (templateId: string) => {
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    setWizard((prev) => ({ ...prev, ...selectTemplatePart(t) }));
  };

  const pickPart = (key: PartKey) => {
    setWizard((prev) => ({ ...prev, part: key, mode: defaultModeFor(key) }));
  };

  const goToStep = (index: number) => {
    if (index < stepIndex) setWizard((prev) => ({ ...prev, step: index }));
  };

  const startLabel = (): string => {
    const part = wizard.part ? partOf(wizard.part) : undefined;
    if (part?.status === 'prog') return 'Resume';
    return 'Start Interview';
  };

  const handleNext = () => {
    if (!canNext()) return;
    setWizard((prev) => ({ ...prev, step: Math.min(steps.length - 1, stepIndex + 1) }));
  };
  const handleBack = () => setWizard((prev) => ({ ...prev, step: Math.max(0, stepIndex - 1) }));

  const handleStart = async () => {
    if (!template || !wizard.part) return;

    if (wizard.part === 'hts') {
      navigate(buildRouteWithQuery(ROUTES.HTS_QUESTIONNAIRE, { templateId: template.id }));
      return;
    }

    setStarting(true);
    setStartError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const selection = includeQuestions ? questionSelection : null;
      // Every question selected is an ordinary full sitting, so it's sent unscoped.
      const itemIds =
        selection && selection.selectedIds.length < template.items.length ? selection.selectedIds : undefined;
      const differentItemIds = selection?.differentIds.length ? selection.differentIds : undefined;
      const assessment = await new AssessmentService(token).createAssessment(template.id, itemIds, differentItemIds);
      await enterAssessment(assessment, { defaultAnswerMode: wizard.mode ?? AnswerMode.Text });
    } catch (err) {
      setStarting(false);
      if (handleIfRevoked(err)) return;
      setStartError(BaseAPIClient.parseErrorMsg(err));
    }
  };

  // A failed template fetch used to fall through to the picker with zero
  // templates — a wizard that can't advance and never says why. Guests hit this
  // hardest: their own launcher used to render the error directly, and they
  // have no other route into the app.
  if (!loadingTemplates && loadError) {
    return (
      <div className={styles.setup} ref={cardRef}>
        <GradientPanel title="Set up your interview" body={PANEL_COPY.locked} />
        <div className={styles.pane}>
          <div className={styles.pbody}>
            <p className={styles.slabel}>GET STARTED</p>
            <h2 className={styles.lockHeading}>We couldn&rsquo;t load your interviews</h2>
            <p className={styles.lockText}>{loadError}</p>
            {onRetryLoad && (
              <div className={styles.foot}>
                <Button className={styles.btnPrimary} onClick={onRetryLoad}>
                  Try again
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!loadingTemplates && !loadError && templates.length === 0) {
    return (
      <div className={styles.setup} ref={cardRef}>
        <GradientPanel title="Set up your interview" body={PANEL_COPY.locked} />
        <div className={styles.pane}>
          <div className={styles.pbody}>
            <p className={styles.slabel}>GET STARTED</p>
            {isGuest ? (
              <>
                <h2 className={styles.lockHeading}>No interview found</h2>
                <p className={styles.lockText}>
                  We couldn&rsquo;t find an interview for your access code. Please reach out to
                  whoever gave you the code.
                </p>
              </>
            ) : (
              <>
                <h2 className={styles.lockHeading}>Unlock a package to begin</h2>
                <p className={styles.lockText}>
                  Each package adds interviews here — HTS, competency, or both. Take them in any order,
                  and every session is scored the way a real board would score it.
                </p>
                <ol className={styles.lockSteps}>
                  <li>
                    <span>1</span>Buy a package that matches the role you&rsquo;re applying for
                  </li>
                  <li>
                    <span>2</span>Its interviews appear in this card, ready to set up
                  </li>
                  <li>
                    <span>3</span>Answer by voice or chat and get scored on every answer
                  </li>
                </ol>
              </>
            )}
          </div>
          {!isGuest && (
            <div className={`${styles.foot} ${styles.footEnd}`}>
              <Button type="primary" size="large" onClick={() => navigate(ROUTES.PACKAGES)}>
                Explore Packages
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const displaySteps = skipPick ? (['pick', ...steps] as StepKey[]) : steps;
  const railSteps = (template ? displaySteps : (['pick', 'which', 'mode', 'preview'] as const)).map((key, i) => {
    if (skipPick && i === 0) {
      return { key, label: STEP_TITLES[key], state: 'done' as const };
    }
    const adjustedIndex = skipPick ? i - 1 : i;
    return {
      key,
      label: STEP_TITLES[key],
      state: (template && adjustedIndex === stepIndex
        ? 'active'
        : template && adjustedIndex < stepIndex
          ? 'done'
          : 'pending') as 'active' | 'done' | 'pending',
    };
  });

  return (
    <div className={styles.setup} ref={cardRef}>
      <GradientPanel
        title="Set up your interview"
        steps={railSteps}
        body={
          stepKey === 'preview' && template?.htsRequired
            ? PANEL_COPY.previewTwoPart
            : stepKey === 'questions' && pickerHistory
              ? PANEL_COPY.questionsRetry
              : PANEL_COPY[stepKey]
        }
        onStepClick={template ? goToStep : undefined}
      />
      <div className={styles.pane}>
        <div className={styles.ptop}>
          <p className={styles.ptitle}>{STEP_TITLES[stepKey]}</p>
        </div>

        <div className={styles.pbody}>
          {stepKey === 'pick' && (
            <>
              <p className={styles.slabel}>SELECT INTERVIEW BELOW</p>
              <button
                type="button"
                className={template ? `${styles.chooser} ${styles.chooserFilled}` : styles.chooser}
                onClick={() => setPickerOpen(true)}
                aria-haspopup="dialog"
              >
                <span className={styles.chooserName}>{template ? template.name : 'Select Interview'}</span>
                <ChevronDown className={styles.chooserChevron} style={{ width: '1em', height: '1em' }} />
              </button>
              {template &&
                (() => {
                  const compQs = enrichedParts.find((p) => p.key === 'comp')?.qs ?? 0;
                  const htsQs = enrichedParts.find((p) => p.key === 'hts')?.qs ?? 0;
                  const compPhrase = `${compQs} competency question${compQs === 1 ? '' : 's'}`;
                  const htsPhrase = `${htsQs} HTS question${htsQs === 1 ? '' : 's'}`;
                  return (
                    <div className={styles.picked}>
                      This interview includes {compPhrase}
                      {template.htsRequired ? <> and {htsPhrase}</> : null}.
                    </div>
                  );
                })()}
            </>
          )}

          {stepKey === 'which' && template && (
            <>
              <p className={styles.slabel}>
                {openParts(enrichedParts).length === 0 ? 'PRACTISE AGAIN — WHICH ONE?' : 'HOW WOULD YOU LIKE TO START?'}
              </p>
              <div className={styles.cards2}>
                {enrichedParts.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={styles.optCard}
                    aria-pressed={wizard.part === p.key}
                    disabled={isLocked(p) || !canAttemptPart(isGuest, p.status)}
                    onClick={() => {
                      if (isLocked(p) || !canAttemptPart(isGuest, p.status)) return;
                      pickPart(p.key);
                    }}
                  >
                    <span>
                      {p.short}
                      <br />
                      Questionnaire
                    </span>
                    <span
                      className={
                        p.status === 'done'
                          ? `${styles.optCardState} ${styles.optCardStateDone}`
                          : p.status === 'prog'
                            ? `${styles.optCardState} ${styles.optCardStateProg}`
                            : `${styles.optCardState} ${styles.optCardStateNew}`
                      }
                    >
                      {partStatusText(p)}
                    </span>
                  </button>
                ))}
              </div>
              {wizard.part && (
                <div className={`${styles.explain} ${styles.explainSelected}`}>
                  {wizard.part === 'hts' ? (
                    <>
                      <b>HTS Questionnaires</b> ask about your background, history and disclosure: the
                      same ground a background investigator would cover, things like past employment,
                      residences and any legal history.
                    </>
                  ) : (
                    <>
                      <b>Competency Questionnaires</b> present scenario questions and score your answer
                      on the judgement behind your decision, how clearly you structure it, and how you
                      deliver it.
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {stepKey === 'questions' && template && (
            <>
              <p className={styles.slabel}>WHICH QUESTIONS DO YOU WANT IN THIS ATTEMPT?</p>
              {questionSelection ? (
                <QuestionPicker
                  items={template.items}
                  history={pickerHistory}
                  selectedIds={questionSelection.selectedIds}
                  differentIds={questionSelection.differentIds}
                  onChange={(selectedIds, differentIds) =>
                    setQuestionChoice({ templateId: template.id, selectedIds, differentIds })
                  }
                  listClassName={styles.pickerList}
                />
              ) : (
                <div className={styles.pickerLoading}>
                  <Spin />
                </div>
              )}
            </>
          )}

          {stepKey === 'mode' && (
            <>
              <p className={styles.slabel}>ANSWER MODE</p>
              <div className={styles.cards2}>
                <button
                  type="button"
                  className={styles.optCard}
                  aria-pressed={wizard.mode === AnswerMode.Voice}
                  onClick={() => setWizard((prev) => ({ ...prev, mode: AnswerMode.Voice }))}
                >
                  <span className={styles.optCardIcon} aria-hidden="true">
                    <Mic size={32} />
                  </span>
                  Voice
                </button>
                <button
                  type="button"
                  className={styles.optCard}
                  aria-pressed={wizard.mode === AnswerMode.Text}
                  onClick={() => setWizard((prev) => ({ ...prev, mode: AnswerMode.Text }))}
                >
                  <span className={styles.optCardIcon} aria-hidden="true">
                    <MessageCircle size={32} />
                  </span>
                  Chat
                </button>
              </div>
              {wizard.mode && (
                <div className={`${styles.explain} ${styles.explainSelected}`}>
                  {wizard.mode === AnswerMode.Voice ? (
                    <>
                      <b>Voice</b> is closest to a real interview: you speak your answer while a timer
                      runs, and it's transcribed and scored the same way a typed answer would be. You
                      can switch to chat at any point during the interview.
                    </>
                  ) : (
                    <>
                      <b>Chat</b> lets you type your answer, then review and edit it for as long as you
                      like before submitting it to be scored. You can switch to voice at any point
                      during the interview.
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {stepKey === 'preview' && template && wizard.part && (() => {
            const part = partOf(wizard.part);
            if (!part) return null;
            return (
              <div className={styles.pv}>
                <p className={styles.pvKicker}>INTERVIEW</p>
                <Title level={2} className={styles.pvHeading}>
                  {template.name}
                </Title>

                {part.status === 'prog' && (
                  <div className={styles.flag}>
                    <b>
                      Resuming — {part.done} of {part.qs} answered
                    </b>
                    You stopped in the {part.short} questionnaire
                    {part.key === 'comp' ? `, answering by ${wizard.mode === AnswerMode.Voice ? 'voice' : 'chat'}` : ''}.
                  </div>
                )}
                {part.status === 'done' && canAttemptPart(isGuest, part.status) && (
                  <div className={`${styles.flag} ${styles.flagDone}`}>
                    <b>New attempt</b>
                    Your previous attempt is kept and both are compared in Results.
                  </div>
                )}

                <div className={styles.pvBlock}>
                  <p className={styles.pvKicker}>ABOUT</p>
                  <p>{template.description || ABOUT_FALLBACK}</p>
                </div>
                <div className={styles.pvBlock}>
                  <p className={styles.pvKicker}>LENGTH</p>
                  <p>
                    {part.status === 'prog'
                      ? `${part.qs - part.done} questions left of ${part.qs}`
                      : includeQuestions && questionSelection && questionSelection.selectedIds.length < part.qs
                        ? `${questionSelection.selectedIds.length} of ${part.qs} ${part.short} questions`
                        : `${part.qs} ${part.short} questions`}
                  </p>
                </div>
                <div className={styles.pvGrid2}>
                  <div>
                    <p className={styles.pvKicker}>YOU WILL START</p>
                    <p>{part.label}</p>
                  </div>
                  <div>
                    <p className={styles.pvKicker}>ANSWER MODE</p>
                    <p>
                      {part.key === 'comp'
                        ? wizard.mode === AnswerMode.Voice
                          ? 'Voice'
                          : 'Chat'
                        : 'Typed — HTS has no voice mode'}
                    </p>
                  </div>
                </div>
                <div className={`${styles.pvBlock} ${styles.pvNotes}`}>
                  <p className={styles.pvKicker}>HOW FEEDBACK WORKS</p>
                  <ul>
                    {feedbackNotes(part.key, template.coachingEnabled, !isGuest, isGuest).map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}

          {startError && <p className={styles.startError}>{startError}</p>}
        </div>

        <div className={styles.foot}>
          {stepIndex > 0 && (
            <Button className={styles.btnGhost} size="large" onClick={handleBack}>
              Back
            </Button>
          )}
          {isLast ? (
            <Button type="primary" size="large" className={styles.btnPrimary} onClick={handleStart} loading={starting}>
              {startLabel()}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              className={styles.btnPrimary}
              onClick={handleNext}
              disabled={!canNext()}
            >
              Next
            </Button>
          )}
        </div>
      </div>

      <InterviewPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        templates={templates}
        assessmentsByTemplate={assessmentsByTemplate}
        onPick={pickInterview}
      />
    </div>
  );
};

export default SetupWizard;
